import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useDescope, useSession, useUser } from '@descope/react-sdk';
import { setUnauthorizedHandler } from '../services/api';

interface AuthUser {
  email: string | null;
  name: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { logout: descopeLogout } = useDescope();
  const { isAuthenticated, isSessionLoading } = useSession();
  const { user: descopeUser } = useUser();

  const logout = () => { void descopeLogout(); };

  // On 401 responses from the API, sign the user out.
  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user: AuthUser | null = isAuthenticated && descopeUser
    ? { email: descopeUser.email ?? null, name: descopeUser.name ?? null }
    : null;

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated, isLoading: isSessionLoading, logout }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, isAuthenticated, isSessionLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
